import { DateTime, Duration } from "luxon";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { VelavuRoute } from "velavu-js-api";
import IconCircleDeleteFilled from "../../../../dynamicicons/icon-circle-delete-filled";
import IconInfo from "../../../../dynamicicons/icon-info";
import IconSpeed from "../../../../dynamicicons/icon-speed";
import { formatDistance, formatSpeed } from "../../../../helper/unit-helper";
import { useSignInContext } from "../../../sign-in-context/sign-in-context";
import * as styles from "./device-routes-detail.module.scss";
import whitelabel from "../../../../whitelabel";
import { useHasPermission } from "../../../sign-in-context/sign-in-context";

interface DeviceRouteDetailProps {
	route: VelavuRoute;
	onDeleteRoute: VoidFunction;
}

export default function DeviceRoutesDetail(props: DeviceRouteDetailProps) {
	const { t } = useTranslation();

	const canWriteAssets = useHasPermission("assets:write");

	//Getting the user
	const userContext = useSignInContext();

	//Parsing the dates
	const dateStart = DateTime.fromISO(props.route.data.time_start);
	const dateEnd = props.route.data.time_stop
		? DateTime.fromISO(props.route.data.time_stop)
		: undefined;

	//Creating the date string
	const labelDate = dateStart.toLocaleString(DateTime.DATE_MED);

	//Creating the duration string
	let duration: Duration;
	let labelDuration: string;

	if (dateEnd) duration = dateEnd.diff(dateStart, ["hours", "minutes"]);
	else duration = DateTime.now().diff(dateStart, ["hours", "minutes"]);

	const hours = Math.floor(duration.hours!);
	const minutes = Math.floor(duration.minutes!);

	if (hours >= 1) {
		labelDuration = `${hours}h ${minutes}m`;
	} else {
		labelDuration = t("dashboard.routes.duration_minutes", {
			count: minutes,
		});
	}

	return (
		<div className={styles.container}>
			<div className={styles.selectionIndicator} />
			<div className={styles.content}>
				<div className={styles.twoLevelIcon}>
					<IconInfo
						size={16}
						color={whitelabel.palette.secondary900}
					/>
					<div className={styles.twoLevel}>
						<span className={styles.label}>
							{t("dashboard.routes.date")}
						</span>
						<span className={styles.value}>{labelDate}</span>
					</div>
				</div>
				<div className={styles.twoLevel}>
					<span className={styles.label}>
						{t("dashboard.routes.distance")}
					</span>
					<span className={styles.value}>
						{formatDistance(
							props.route.data.distance,
							userContext.preferences.measurementSystem,
						)}
					</span>
				</div>
				<div className={styles.twoLevel}>
					<span className={styles.label}>
						{t("dashboard.routes.duration")}
					</span>
					<span className={styles.value}>{labelDuration}</span>
				</div>
				<div className={styles.twoLevel}>
					<span className={styles.label}>
						{t("dashboard.routes.events")}
					</span>
					<span
						className={`${styles.value} ${
							props.route.data.event_cnt > 0
								? styles.valueWarn
								: ""
						}`}
					>
						{props.route.data.event_cnt}
					</span>
				</div>

				<div className={styles.twoLevelIcon}>
					<IconSpeed
						size={16}
						color={whitelabel.palette.secondary900}
					/>
					<div className={styles.twoLevel}>
						<span className={styles.label}>
							{t("dashboard.routes.avg_speed")}
						</span>
						<span className={styles.value}>
							{formatSpeed(
								props.route.data.avg_speed,
								userContext.preferences.measurementSystem,
							)}
						</span>
					</div>
				</div>
				<div className={styles.twoLevel}>
					<span className={styles.label}>
						{t("dashboard.routes.max_speed")}
					</span>
					<span className={styles.value}>
						{formatSpeed(
							props.route.data.max_speed,
							userContext.preferences.measurementSystem,
						)}
					</span>
				</div>
				{canWriteAssets && props.route.data.location_stop && (
					<button
						className={styles.deleteButton}
						onClick={props.onDeleteRoute}
					>
						<IconCircleDeleteFilled
							color={whitelabel.palette.secondary700}
							size={16}
						/>
						<span className={styles.buttonText}>
							{t("dashboard.routes.delete")}
						</span>
					</button>
				)}
			</div>
		</div>
	);
}
